import React, { useEffect, useState, useCallback } from "react";
import { api, formatDateTime, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Battery, Wifi, WifiOff, Zap, Gauge, MapPin, RefreshCw, Loader2,
  ArrowUpCircle, ArrowDownCircle, AlertCircle, Circle,
} from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function StatDot({ ok }) {
  return <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />;
}

/** Live IoT panel for a single device. Handles all provider errors. */
export function IoTDevicePanel({ deviceId, autoRefreshMs = 45000 }) {
  const [dev, setDev] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get(`/iot/devices/${deviceId}`);
      setDev(data.device); setFetchedAt(new Date());
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(formatApiError(d?.error || d) || e.message);
    } finally { setLoading(false); setBusy(false); }
  }, [deviceId]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => {
    if (!autoRefreshMs) return;
    const t = setInterval(load, autoRefreshMs);
    return () => clearInterval(t);
  }, [load, autoRefreshMs]);

  if (loading) return <Card className="p-5 rounded-xl"><div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Fetching live IoT data…</div></Card>;
  if (err) return (
    <Card className="p-5 rounded-xl border-amber-500/40">
      <div className="flex items-center gap-2 text-amber-500 text-sm"><AlertCircle className="w-4 h-4" /> Live IoT unavailable</div>
      <div className="text-xs text-muted-foreground mt-1">{err}</div>
      <Button variant="outline" size="sm" className="mt-3" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry</Button>
    </Card>
  );
  if (!dev) return null;

  const online = dev.status === "online";
  return (
    <Card className="p-5 rounded-xl" data-testid={`iot-panel-${deviceId}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Live IoT — Flexi Twin</div>
          <div className="mt-1 flex items-center gap-2">
            <div className="font-heading text-lg font-semibold">{dev.name || dev.unique_id || `Device ${dev.device_id}`}</div>
            {online ? (
              <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30 gap-1"><StatDot ok /> Online</Badge>
            ) : (
              <Badge variant="secondary" className="gap-1"><WifiOff className="w-3 h-3" /> Offline</Badge>
            )}
            {dev.charging === true && <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/30 gap-1"><ArrowUpCircle className="w-3 h-3" /> Charging</Badge>}
            {dev.charging === false && <Badge variant="outline" className="gap-1"><ArrowDownCircle className="w-3 h-3" /> Discharging</Badge>}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Device #{dev.device_id} · UID {dev.unique_id} · last ping {timeAgo(dev.last_updated)}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="iot-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* metric grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric icon={Battery} label="State of Charge" value={dev.soc != null ? `${dev.soc}%` : "—"} accent="emerald" />
        <Metric icon={Zap} label="Voltage" value={dev.voltage != null ? `${dev.voltage} V` : "—"} accent="blue" />
        <Metric icon={Gauge} label="Current" value={dev.current != null ? `${dev.current} A` : "—"} accent="fuchsia" />
        <Metric icon={Zap} label="Power (derived)" value={dev.power != null ? `${dev.power} W` : "—"} accent="amber" />
      </div>

      {(dev.latitude != null && dev.longitude != null) && (
        <div className="mt-4 flex items-center gap-2 text-sm">
          <MapPin className="w-4 h-4 text-rose-500" />
          <a className="text-blue-500 hover:underline" target="_blank" rel="noreferrer"
             href={`https://www.google.com/maps?q=${dev.latitude},${dev.longitude}`}>
            {dev.latitude.toFixed(5)}, {dev.longitude.toFixed(5)}
          </a>
        </div>
      )}

      {/* Fields NOT provided by the Flexi Twin API */}
      <details className="mt-4 group">
        <summary className="text-xs text-muted-foreground cursor-pointer">Fields not provided by the current IoT API</summary>
        <div className="text-xs text-muted-foreground mt-2 grid grid-cols-2 md:grid-cols-3 gap-1">
          <div>SoH: <span className="text-foreground/50">not supported</span></div>
          <div>Temperature: <span className="text-foreground/50">not supported</span></div>
          <div>Faults / Alarms: <span className="text-foreground/50">not supported</span></div>
          <div>History / Telemetry: <span className="text-foreground/50">not supported</span></div>
        </div>
      </details>

      <div className="mt-3 text-[10px] text-muted-foreground">Last updated: {fetchedAt ? fetchedAt.toLocaleTimeString() : "—"} · auto-refresh every {Math.round(autoRefreshMs / 1000)}s</div>
    </Card>
  );
}

function Metric({ icon: Icon, label, value, accent }) {
  const colors = {
    blue: "text-blue-500 bg-blue-500/10",
    emerald: "text-emerald-500 bg-emerald-500/10",
    amber: "text-amber-500 bg-amber-500/10",
    fuchsia: "text-fuchsia-500 bg-fuchsia-500/10",
  };
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <span className={`w-6 h-6 rounded grid place-items-center ${colors[accent]}`}><Icon className="w-3.5 h-3.5" /></span>
        {label}
      </div>
      <div className="font-heading text-xl font-semibold mt-1">{value}</div>
    </div>
  );
}
